/*
 * Chat.jsx — Iteration 65 multi-document chat sessions.
 *
 * Left sidebar : list of the current user's sessions + "New conversation" CTA.
 * Middle / right : either the document picker (when creating) or the active
 *                  conversation (messages + input).
 *
 * Backend contract:
 *   POST   /api/chat/sessions          { document_ids, title? }        -> session
 *   GET    /api/chat/sessions                                          -> {sessions,is_mock,max_*}
 *   GET    /api/chat/sessions/{id}                                     -> session w/ messages
 *   POST   /api/chat/sessions/{id}/messages { question }               -> {user_message, assistant_message, questions_used, ...}
 *   POST   /api/chat/sessions/{id}/messages/stream { question }        -> text/event-stream
 *                                                                          event: delta {text}  (repeated)
 *                                                                          event: done  {message_id, user_message, citations, confidence, out_of_scope, is_mock, questions_used, max_questions}
 *                                                                          event: error {detail}
 *   DELETE /api/chat/sessions/{id}                                     -> 204
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Trash2, Send, Loader2, FileText, X, Sparkles,
  CheckCircle2, ExternalLink, AlertTriangle,
} from "lucide-react";

import { api, API, WS_STORAGE_KEY, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { ScrollPanel } from "../components/ui/scroll-panel";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import { useTour } from "../lib/tours/useTour";
import { useChatSteps } from "../lib/tours/steps/chat";
import TourReplayButton from "../lib/tours/TourReplayButton";

// ---------------------------------------------------------------------------
// SSE helper — parses one "event: X\ndata: Y" block (fetch() + native
// ReadableStream; no EventSource since it can't do POST with credentials).
// ---------------------------------------------------------------------------
function parseSSEEvent(rawEvent) {
  let eventName = "message";
  const dataLines = [];
  for (const line of rawEvent.split("\n")) {
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  const raw = dataLines.join("\n");
  if (!raw) return null;
  try {
    return { event: eventName, data: JSON.parse(raw) };
  } catch (_) {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sub-component : session card in the left sidebar
// ---------------------------------------------------------------------------
function SessionListItem({ session, active, onSelect, onAskDelete }) {
  const { t } = useTranslation("chat");
  const ns = "sessions_page";
  const updated = new Date(session.updated_at || session.created_at);
  return (
    <div
      className={`group relative rounded-xl border transition-colors cursor-pointer ${
        active
          ? "border-qenva-cat-invoice/60 bg-qenva-cat-invoice/5"
          : "border-zinc-200 bg-white hover:border-zinc-300"
      }`}
      data-testid={`chat-session-item-${session.id}`}
      onClick={onSelect}
    >
      <div className="p-3 pr-9">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare size={13} strokeWidth={1.75} className="text-zinc-500 shrink-0" />
          <p className="text-sm font-semibold text-zinc-950 truncate">
            {session.title || t(`${ns}.new_session`)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-zinc-500">
            {updated.toLocaleDateString(undefined, {
              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </span>
          {session.is_mock && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 border-amber-300 text-amber-700 bg-amber-50"
              data-testid="chat-session-mock-badge"
            >
              {t(`${ns}.message_mock_badge`)}
            </Badge>
          )}
          <span className="text-[11px] text-zinc-400">
            · {session.questions_used}Q
          </span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`chat-session-delete-${session.id}`}
        onClick={(e) => { e.stopPropagation(); onAskDelete(session); }}
        aria-label={t(`${ns}.delete_button`)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 grid place-items-center rounded-md hover:bg-zinc-100 text-zinc-500"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component : document picker (multi-select)
// ---------------------------------------------------------------------------
function DocumentPicker({ documents, maxDocs, onStart, onCancel }) {
  const { t } = useTranslation("chat");
  const ns = "sessions_page";
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return documents;
    return documents.filter((d) =>
      (d.filename || "").toLowerCase().includes(term)
      || (d.document_type || "").toLowerCase().includes(term));
  }, [documents, q]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxDocs) {
        toast.error(t(`${ns}.picker_subtitle`, { max: maxDocs }));
        return prev;
      }
      return [...prev, id];
    });
  }, [maxDocs, t]);

  const start = async () => {
    if (!selected.length) return;
    setCreating(true);
    try {
      await onStart(selected);
    } finally {
      setCreating(false);
    }
  };

  if (!documents.length) {
    return (
      <div
        className="rounded-xl border border-zinc-200 bg-white p-8 text-center"
        data-testid="chat-picker-empty"
      >
        <FileText size={22} className="mx-auto text-zinc-400 mb-3" />
        <p className="text-sm text-zinc-600">{t(`${ns}.picker_no_documents`)}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="chat-picker">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-950">
            {t(`${ns}.picker_title`)}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {t(`${ns}.picker_subtitle`, { max: maxDocs })}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          data-testid="chat-picker-cancel"
          className="w-7 h-7 grid place-items-center rounded-md hover:bg-zinc-100 text-zinc-500"
        >
          <X size={14} />
        </button>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t(`${ns}.picker_placeholder`)}
        data-testid="chat-picker-search"
        className="h-9 text-sm mb-3"
      />

      <ScrollArea className="h-[300px] pr-3">
        <div className="space-y-1.5" data-testid="chat-picker-list">
          {filtered.map((d) => {
            const checked = selected.includes(d.id);
            return (
              <label
                key={d.id}
                htmlFor={`chat-picker-doc-${d.id}`}
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  checked
                    ? "border-qenva-cat-invoice/60 bg-qenva-cat-invoice/5"
                    : "border-zinc-200 hover:border-zinc-300 bg-white"
                }`}
                data-testid={`chat-picker-doc-${d.id}`}
              >
                <Checkbox
                  id={`chat-picker-doc-${d.id}`}
                  checked={checked}
                  onCheckedChange={() => toggle(d.id)}
                  data-testid={`chat-picker-checkbox-${d.id}`}
                />
                <FileText size={14} className="text-zinc-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-950 truncate">{d.filename}</p>
                  <p className="text-[11px] text-zinc-500">{d.document_type}</p>
                </div>
              </label>
            );
          })}
        </div>
      </ScrollArea>

      <div className="mt-4 flex items-center justify-between gap-3 pt-3 border-t border-zinc-100">
        <p className="text-xs text-zinc-500" data-testid="chat-picker-selected-count">
          {selected.length === 1
            ? t(`${ns}.picker_selected_count_one`, { count: selected.length })
            : t(`${ns}.picker_selected_count_other`, { count: selected.length })}
        </p>
        <Button
          onClick={start}
          disabled={!selected.length || creating}
          data-testid="chat-picker-start"
          className="h-9 bg-zinc-950 hover:bg-zinc-800 text-white"
        >
          {creating
            ? <><Loader2 size={12} className="animate-spin mr-1.5" />…</>
            : <>{t(`${ns}.picker_start_button`)}</>}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component : one message (user OR assistant, w/ citations)
// ---------------------------------------------------------------------------
function MessageBubble({ msg, docLookup }) {
  const { t } = useTranslation("chat");
  const ns = "sessions_page";
  const isUser = msg.role === "user";
  const confidence = msg.confidence || "medium";
  const confLabel = t(`${ns}.message_confidence_${confidence}`);

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chat-message-${msg.role}`}
    >
      <div className={`max-w-[85%] ${isUser ? "" : "w-full"}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
            {isUser ? t(`${ns}.message_you`) : t(`${ns}.message_assistant`)}
          </span>
          {!isUser && msg.is_mock && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 border-amber-300 text-amber-700 bg-amber-50"
              title={t(`${ns}.message_mock_tooltip`)}
              data-testid="chat-message-mock-badge"
            >
              {t(`${ns}.message_mock_badge`)}
            </Badge>
          )}
          {!isUser && msg.out_of_scope && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 border-red-300 text-red-700 bg-red-50"
              data-testid="chat-message-oos-badge"
            >
              <AlertTriangle size={9} className="mr-1" />
              {t(`${ns}.message_out_of_scope_badge`)}
            </Badge>
          )}
          {!isUser && (
            <span className="text-[10px] text-zinc-400" title={confLabel}>
              · <CheckCircle2 size={9} className="inline mr-0.5" />
              {confLabel}
            </span>
          )}
        </div>
        <div
          className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-zinc-950 text-white rounded-br-sm"
              : "bg-white border border-zinc-200 text-zinc-950 rounded-bl-sm"
          }`}
        >
          {msg.content}
        </div>
        {!isUser && msg.citations && msg.citations.length > 0 && (
          <div className="mt-2" data-testid="chat-message-citations">
            <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
              {t(`${ns}.message_citations_title`)}
            </p>
            <div className="space-y-1.5">
              {msg.citations.map((c, i) => {
                const doc = docLookup[c.document_id];
                return (
                  <div
                    key={i}
                    className="p-3 rounded-lg border border-zinc-200 bg-zinc-50/50"
                    data-testid={`chat-citation-${i}`}
                  >
                    <blockquote className="text-[13px] text-zinc-800 border-l-2 border-qenva-cat-invoice/40 pl-3 italic">
                      « {c.quote} »
                    </blockquote>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-zinc-500">
                        {t(`${ns}.message_citation_from`)}{" "}
                        <span className="font-medium text-zinc-700">
                          {doc?.filename || c.document_id.slice(0, 8)}
                        </span>
                      </span>
                      {doc && (
                        <Link
                          to={`/analysis/${c.document_id}`}
                          className="text-[11px] text-qenva-cat-invoice hover:underline inline-flex items-center gap-1"
                          data-testid={`chat-citation-view-${c.document_id}`}
                        >
                          {t(`${ns}.message_citation_view_doc`)}
                          <ExternalLink size={10} />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component : active conversation panel
// ---------------------------------------------------------------------------
function ConversationPanel({ session, maxQuestions, onSend, sending, streamingText,
                              suggestions, suggestionsSource, suggestionsLoading }) {
  const { t } = useTranslation("chat");
  const ns = "sessions_page";
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const docLookup = useMemo(() => {
    const map = {};
    (session.document_snapshots || []).forEach((d) => { map[d.id] = d; });
    return map;
  }, [session]);

  // A sentinel at the end of the list + scrollIntoView, rather than manually
  // computing scrollTop/scrollHeight — during token-by-token streaming the
  // latter can read stale layout numbers and fall behind, leaving the
  // in-progress answer scrolled out of view above the fold. useLayoutEffect
  // (not useEffect) so this runs synchronously right after the DOM commit,
  // before the browser paints — keeps the streamed text pinned to the
  // bottom, next to the input, with no visible lag.
  useLayoutEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
  }, [session.messages, sending, streamingText]);

  const limitReached = (session.questions_used || 0) >= maxQuestions;
  const handleSend = async () => {
    const val = text.trim();
    if (!val) {
      toast.error(t(`${ns}.input_empty_error`));
      return;
    }
    setText("");
    await onSend(val);
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-zinc-50 rounded-xl border border-zinc-200" data-testid="chat-conversation">
      {/* Attached docs strip */}
      <div className="p-3 border-b border-zinc-200 bg-white rounded-t-xl">
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
          {t(`${ns}.attached_title`)}
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="chat-attached-docs">
          {(session.document_snapshots || []).map((d) => (
            <Link
              key={d.id}
              to={`/analysis/${d.id}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-100 hover:bg-zinc-200 transition-colors text-[11.5px] text-zinc-800"
              data-testid={`chat-attached-doc-${d.id}`}
            >
              <FileText size={11} className="text-zinc-500" />
              <span className="truncate max-w-[220px]">{d.filename}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Messages */}
      <ScrollPanel className="p-5 space-y-4" data-testid="chat-messages">
        {(session.messages || []).length === 0 && (
          <div
            className="text-center py-16"
            data-testid="chat-empty-conversation"
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-white border border-zinc-200 grid place-items-center mb-3">
              <Sparkles size={18} className="text-qenva-cat-invoice" />
            </div>
            <p className="font-display text-lg font-semibold text-zinc-950">
              {t(`${ns}.empty_conversation_title`)}
            </p>
            {suggestions && suggestions.length > 0 ? (
              <div className="mt-4 max-w-md mx-auto space-y-2 text-left" data-testid="chat-session-suggestions">
                <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5 text-center">
                  {t("suggestions_ui.header")}
                  {suggestionsSource === "llm" ? ` · ${t("suggestions_ui.source_llm")}` : ""}
                  {suggestionsSource === "mock" ? ` · ${t("suggestions_ui.source_fallback")}` : ""}
                </p>
                {suggestions.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => onSend(q)}
                    disabled={sending}
                    className="w-full text-left text-sm px-3 py-2 rounded-lg border border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid={`chat-session-suggestion-${i}`}
                  >
                    <Sparkles size={11} className="text-zinc-400 shrink-0" />
                    <span className="text-zinc-800">{q}</span>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
                  {t(`${ns}.empty_conversation_subtitle`)}
                </p>
                {suggestionsLoading && (
                  <p
                    className="text-[11px] text-zinc-400 mt-3 inline-flex items-center gap-1.5 justify-center w-full"
                    data-testid="chat-session-suggestions-loading"
                  >
                    <Loader2 size={10} className="animate-spin" />
                    {t("suggestions_ui.header")}…
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {(session.messages || []).map((m) => (
          <MessageBubble key={m.id} msg={m} docLookup={docLookup} />
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-sending-indicator">
            {streamingText ? (
              <MessageBubble
                msg={{
                  id: "streaming",
                  role: "assistant",
                  content: streamingText,
                  citations: [],
                  confidence: "medium",
                  out_of_scope: false,
                  is_mock: false,
                }}
                docLookup={docLookup}
              />
            ) : (
              <div className="px-4 py-3 rounded-2xl bg-white border border-zinc-200 text-sm text-zinc-500 rounded-bl-sm inline-flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                {t(`${ns}.input_sending`)}
              </div>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </ScrollPanel>

      {/* Input */}
      <div className="p-3 border-t border-zinc-200 bg-white rounded-b-xl">
        {limitReached ? (
          <div
            className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900"
            data-testid="chat-limit-reached"
          >
            {t(`${ns}.input_limit_reached`, { max: maxQuestions })}
          </div>
        ) : (
          <>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t(`${ns}.input_placeholder`)}
              rows={2}
              disabled={sending}
              data-testid="chat-input"
              className="text-sm resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-[11px] text-zinc-400">{t(`${ns}.input_hint`)}</p>
              <Button
                onClick={handleSend}
                disabled={sending || !text.trim()}
                data-testid="chat-send-button"
                className="h-9 bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                {sending ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />}
                {sending ? t(`${ns}.input_sending`) : t(`${ns}.input_send_button`)}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Chat() {
  const { t } = useTranslation("chat");
  const ns = "sessions_page";
  const [sessions, setSessions] = useState([]);
  const [meta, setMeta] = useState({
    is_mock: false, max_questions_per_session: 40, max_docs_per_session: 5,
  });
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [creating, setCreating] = useState(false);        // show picker
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsSource, setSuggestionsSource] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.get("/chat/sessions");
      setSessions(res.data.sessions || []);
      setMeta({
        is_mock: res.data.is_mock,
        max_questions_per_session: res.data.max_questions_per_session,
        max_docs_per_session: res.data.max_docs_per_session,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "load failed");
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await api.get("/documents");
      // Filter: only completed docs (analyzed OR manual) with an ID
      const list = (res.data?.items || res.data || [])
        .filter((d) => !d.is_deleted && d.id && d.status === "completed");
      setDocuments(list);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadDocuments();
  }, [loadSessions, loadDocuments]);

  // 2-3 questions synthesized across every document attached to the
  // session — only meaningful (and only fetched) while the session has no
  // messages yet; see routes/chat.py's GET .../suggestions. Best-effort:
  // a failure here just leaves the generic empty-state copy in place.
  const loadSuggestions = useCallback(async (id) => {
    setSuggestionsLoading(true);
    try {
      const res = await api.get(`/chat/sessions/${id}/suggestions`);
      setSuggestions(res.data.questions || []);
      setSuggestionsSource(res.data.source || null);
    } catch (err) {
      setSuggestions([]);
      setSuggestionsSource(null);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (id) => {
    try {
      const res = await api.get(`/chat/sessions/${id}`);
      setActiveSession(res.data);
      setActiveSessionId(id);
      setCreating(false);
      if ((res.data.messages || []).length === 0) {
        loadSuggestions(id);
      } else {
        setSuggestions([]);
        setSuggestionsSource(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message);
    }
  }, [loadSuggestions]);

  const startNewSession = () => {
    setActiveSessionId(null);
    setActiveSession(null);
    setCreating(true);
  };

  const createSession = async (documentIds) => {
    try {
      const res = await api.post("/chat/sessions", { document_ids: documentIds });
      await loadSessions();
      setActiveSession(res.data);
      setActiveSessionId(res.data.id);
      setCreating(false);
      loadSuggestions(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message);
    }
  };

  // Streams the answer via SSE. Uses fetch() + a raw ReadableStream reader
  // instead of EventSource because EventSource can't send credentials/custom
  // headers on a POST. Auth still travels via the httpOnly cookie (same as
  // the axios instance's withCredentials) — X-Workspace-Id is added by hand
  // since this bypasses the axios interceptor. Note: unlike `api`, this path
  // does not retry on 401 with a token refresh — a mid-stream auth expiry
  // just surfaces as an error toast, which is an acceptable trade-off for a
  // long-lived streaming call.
  const sendMessage = async (question) => {
    if (!activeSessionId) return;
    setSending(true);
    setStreamingText("");
    // Optimistic append of the user message so the UI feels snappy.
    setActiveSession((prev) => prev && ({
      ...prev,
      messages: [...(prev.messages || []),
        { id: `local-${Date.now()}`, role: "user", content: question, ts: new Date().toISOString() }],
    }));

    const fail = async (detail) => {
      toast.error(`${t(`${ns}.input_send_failed`)} — ${detail}`);
      await loadSession(activeSessionId);
    };

    let response;
    try {
      const wsid = localStorage.getItem(WS_STORAGE_KEY);
      response = await fetch(`${API}/chat/sessions/${activeSessionId}/messages/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(wsid ? { "X-Workspace-Id": wsid } : {}),
        },
        body: JSON.stringify({ question }),
      });
    } catch (err) {
      await fail(err?.message || "network error");
      setSending(false);
      return;
    }

    if (!response.ok || !response.body) {
      let detail = response.statusText;
      try {
        const errJson = await response.json();
        detail = formatApiErrorDetail(errJson?.detail) || detail;
      } catch (_) { /* body wasn't JSON — keep statusText */ }
      await fail(detail);
      setSending(false);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIndex;
        while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          const evt = parseSSEEvent(rawEvent);
          if (!evt) continue;
          if (evt.event === "delta") {
            setStreamingText((prev) => prev + (evt.data.text || ""));
          } else if (evt.event === "done") {
            await loadSession(activeSessionId);
            loadSessions();
          } else if (evt.event === "error") {
            await fail(evt.data.detail || "stream error");
          }
        }
      }
    } catch (err) {
      await fail(err?.message || "stream interrupted");
    } finally {
      setSending(false);
      setStreamingText("");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/chat/sessions/${pendingDelete.id}`);
      toast.success(t(`${ns}.delete_success`));
      if (activeSessionId === pendingDelete.id) {
        setActiveSessionId(null);
        setActiveSession(null);
      }
      await loadSessions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message);
    } finally {
      setPendingDelete(null);
    }
  };

  const chatSteps = useChatSteps();
  const { Tour, replay } = useTour("chat", chatSteps);

  return (
    <div className="animate-fade-up" data-testid="chat-page">
      {Tour}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {t(`${ns}.page_kicker`)}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">
            {t(`${ns}.page_title`)}
          </h1>
          <p className="text-sm text-zinc-500 mt-1.5 max-w-2xl">
            {t(`${ns}.page_subtitle`)}
          </p>
        </div>
        <TourReplayButton onClick={replay} />
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Left — sessions list */}
        <aside className="space-y-3" data-testid="chat-sidebar">
          <Button
            onClick={startNewSession}
            data-testid="chat-new-session"
            className="w-full h-10 bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <Plus size={14} className="mr-1.5" />
            {t(`${ns}.new_session`)}
          </Button>

          <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold px-1">
            {t(`${ns}.sessions_list_title`)}
          </p>

          {sessions.length === 0 && (
            <div className="p-4 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 text-center">
              <p className="text-xs text-zinc-500" data-testid="chat-sessions-empty">
                {t(`${ns}.empty_sessions_hint`)}
              </p>
            </div>
          )}

          <div className="space-y-2" data-testid="chat-sessions-list">
            {sessions.map((s) => (
              <SessionListItem
                key={s.id}
                session={s}
                active={s.id === activeSessionId}
                onSelect={() => loadSession(s.id)}
                onAskDelete={setPendingDelete}
              />
            ))}
          </div>
        </aside>

        {/* Right — picker OR conversation OR empty state */}
        <section className="min-h-[600px]" data-testid="chat-main">
          {creating && (
            <DocumentPicker
              documents={documents}
              maxDocs={meta.max_docs_per_session}
              onStart={createSession}
              onCancel={() => setCreating(false)}
            />
          )}
          {!creating && activeSession && (
            <div className="h-[calc(100vh-260px)]">
              <ConversationPanel
                session={activeSession}
                maxQuestions={meta.max_questions_per_session}
                onSend={sendMessage}
                sending={sending}
                streamingText={streamingText}
                suggestions={suggestions}
                suggestionsSource={suggestionsSource}
                suggestionsLoading={suggestionsLoading}
              />
            </div>
          )}
          {!creating && !activeSession && (
            <div
              className="h-full grid place-items-center rounded-xl border border-dashed border-zinc-300 bg-zinc-50 min-h-[500px]"
              data-testid="chat-idle-placeholder"
            >
              <div className="text-center max-w-md">
                <div className="w-12 h-12 mx-auto rounded-full bg-white border border-zinc-200 grid place-items-center mb-3">
                  <MessageSquare size={18} className="text-qenva-cat-invoice" />
                </div>
                <p className="font-display text-lg font-semibold text-zinc-950">
                  {t(`${ns}.page_title`)}
                </p>
                <p className="text-sm text-zinc-500 mt-1">{t(`${ns}.page_subtitle`)}</p>
                <Button
                  onClick={startNewSession}
                  className="mt-5 h-10 bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="chat-empty-cta"
                >
                  <Plus size={14} className="mr-1.5" />
                  {t(`${ns}.new_session`)}
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="chat-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t(`${ns}.delete_confirm_title`)}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${ns}.delete_confirm_body`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chat-delete-cancel">
              {t(`${ns}.delete_cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="chat-delete-confirmed"
              className="bg-red-600 hover:bg-red-700"
            >
              {t(`${ns}.delete_confirmed`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
